import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { EMPLOYEE_MANY } from "../../schema/employee";
import { Link } from "react-router-dom";
import { Competence, Employee } from "../../types";

const FindSimilarEmployees: React.FC = () => {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const { data, loading, error } = useQuery(EMPLOYEE_MANY);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading employees.</div>;

  const employees: Employee[] = data?.employeeMany || [];
  const selectedEmployee = employees.find(e => e._id === selectedEmployeeId);

  function getSimilarity(a: Employee, b: Employee): number {
    if (!a.competences || !b.competences) return 0;
    const aSkills = new Map(a.competences.map((c: Competence) => [c.skillId, c.value]));
    const bSkills = new Map(b.competences.map((c: Competence) => [c.skillId, c.value]));
    let common = 0;
    let total = 0;
    aSkills.forEach((aValue, skillId) => {
      if (bSkills.has(skillId)) {
        const bValue = bSkills.get(skillId);
        if (typeof bValue === "number") {
          common += 1 - Math.abs(aValue - bValue) / 100;
        }
      }
      total++;
    });
    return total > 0 ? common / total : 0;
  }

  const similarEmployees = selectedEmployee
    ? employees
        .filter(e => e._id !== selectedEmployeeId)
        .map(e => ({ ...e, similarity: getSimilarity(selectedEmployee, e) }))
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, 10)
    : [];

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Finde ähnliche Mitarbeiter nach Skills</h2>
      <label className="block mb-2">Mitarbeiter auswählen:</label>
      <select
        className="mb-6 p-2 border rounded w-full"
        value={selectedEmployeeId}
        onChange={e => setSelectedEmployeeId(e.target.value)}
      >
        <option value="">-- Bitte wählen --</option>
        {employees.map(e => (
          <option key={e._id} value={e._id}>
            {e.firstName} {e.lastName}
          </option>
        ))}
      </select>
      {selectedEmployee && (
        <div>
          <h3 className="text-lg font-semibold mb-2">Ähnliche Mitarbeiter:</h3>
          <ul className="divide-y divide-gray-200">
            {similarEmployees.map(e => (
              <li key={e._id} className="py-2 flex justify-between items-center">
                <Link
                  to={`/employees/${e._id}/details`}
                  className="text-blue-600 hover:underline"
                >
                  {e.firstName} {e.lastName}
                </Link>
                <span className="text-sm text-gray-500">Ähnlichkeit: {(e.similarity * 100).toFixed(2)}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FindSimilarEmployees;
